import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import Filter from '@/pages/dashboard/components/NewFilter';
import { setBtnPointLog } from "@/pages/dashboard/utils/utils";
import {  getLocale, useSelector, history } from 'umi';
import DateComparesionExralFilter from './dateComparesionExralFilter';
import { renderMetric } from './filterList';
import { pcDateComparisonDefaultSelector } from './_constant';
import { useFilterData } from './hooks';
import { getDateDefault } from '../../utils/dateUtils';
import { TIME_LIMIT_EXCLUDED_GAMES } from '../../utils/_config/constant';
import { useFrontMetricsOverride } from '@/pages/dashboard/hooks/useFrontMetricsList';
import {
  pickFilterViewType,
  resolveDashboardSelectorFilters,
} from '../common/pageSelectorUtils';

/**
 *
 * @param props
 * @param props:filterConfirm 筛选器确认函数
 * @param props:filterReset 筛选器重置函数
 * @param props:setFilterLoading 筛选器loading设置，透传到筛选器组件内部，获取到筛选器列表时区等数据之后，设置为false，测试才可进行页面和组件中的接口调用
 * @param props:filterInit 初始化函数，会向外部传递初始化后的筛选器值
 * @returns
 */

// 筛选器路径
const FILTER_PATH = 'comparisonAnalysis/eventComparison';

const PcConsoleDateComparisonFilter = (props: any) => {
  const { filterConfirm, filterReset, buttonLogConfig, setFilterLoading, filterInit, path = FILTER_PATH, openSticky = false, children, isHaveFilterSave, timeZone = 0, config  } = props;
  const [isReseted, setIsReseted] = useState<boolean>(false); // 是否重置过
  const [overRangeTimeLimitStatus, setOverRangeTimeLimitStatus] = useState<{[key: string]: boolean;}>({}); // 时间范围超限状态

  const { gameConfig, currentGame, metricsInfo } = useSelector((state: any) => ({
    gameConfig: state?.dashboardSetting?.gameConfig || {},
    currentGame: state.dashboard?.currentGame || {},
    metricsInfo: state.dashboard?.metricsInfo || {},
  }));

  const {timeLimit} = history.location.query || {};
  const isNotLimitTime = useMemo(
    () =>
      timeLimit === 'false' ||
      TIME_LIMIT_EXCLUDED_GAMES.includes(gameConfig?.game_code),
    [timeLimit, gameConfig?.game_code],
  );

  // daily筛选器继承逻辑
  const filterValueRef = useRef<any>(null); // 筛选器值，此处不用state，更新formValue同样会触发组件更新
  // 事件数量低于2时的默认时间
  const defaultStaticsDate = {
    type: 'date',
    key: 'rangeTime',
    filterId: 'statiscDate1',
    name: 'Statistics Date',
    value: getDateDefault({ timeType: 'day', timeZone, startNum: 14, endNum: 1}),
  };
  const defaultContrastDate = {
    type: 'date',
    key: 'rangeTime',
    filterId: 'contrastDate1',
    name: `Contrast Date1`,
    value: getDateDefault({ timeType: 'day', timeZone, startNum: 28, endNum: 15}),
  };

  // 增加state解决handleReset的闭包问题
  const [defaultDates, setDefaultDates] = useState({
    defaultStaticsDate,
    defaultContrastDate,
  });

  const [statiscDate, setStaticsDate] = useState(defaultStaticsDate);
  const [contrastDate, setContrastDate] = useState([defaultContrastDate]);

  const { loading, selector } = useFilterData(path,'daily', pcDateComparisonDefaultSelector, true);
  const { mergedSelector } = useFrontMetricsOverride(config, selector, metricsInfo);
  const { latestDate } = mergedSelector || {};

  const pageFilters = useMemo(
    () => resolveDashboardSelectorFilters(gameConfig, path),
    [gameConfig, path],
  );

  // 合并 page_selector 的 view_type / viewType（isUseConfig 时 NewFilter 会保留 item 上已有字段）
  const inputList = useMemo(() => {
    const pick = (key: string): string | undefined => {
      return pickFilterViewType(
        pageFilters.find((x: any) => x?.key === key) || {},
      );
    };
    return [
      {
        key: 'metrics',
        type: 'metrics',
        isUseConfig: true,
        default: ['active_users', 'new_users'],
        render: renderMetric,
        ...(pick('metrics') ? { view_type: pick('metrics') } : {}),
      },
      {
        type: 'zone',
        key: 'zone',
        isUseConfig: true,
        default: ['255'],
        ...(pick('zone') ? { view_type: pick('zone') } : {}),
      },
      {
        type: 'region',
        key: 'region',
        isUseConfig: true,
        default: {
          markets: ['255'],
          region_type: 'market',
        },
        ...(pick('region') ? { view_type: pick('region') } : {}),
      },
      {
        type: 'channel',
        key: 'channel',
        isUseConfig: true,
        default: ['255'],
        ...(pick('channel') ? { view_type: pick('channel') } : {}),
      },
      {
        type: 'system',
        key: 'system',
        isUseConfig: true,
        default: ['255'],
        view_type: pick('system') || 'group',
      },
      {
        type: 'lang',
        key: 'lang',
        isUseConfig: true,
        default: ['255'],
        ...(pick('lang') ? { view_type: pick('lang') } : {}),
      },
    ];
  }, [pageFilters]);

  const handleConfirm = (value: any) => {
    filterValueRef.current = value;
    setIsReseted(false);

    const comparisonDate = [{
      name: statiscDate?.name,
      type: 'date',
      filterId: statiscDate?.filterId,
      value: statiscDate?.value
    }, ...contrastDate]?.map((item: any) => ({
      name: item?.name,
      start_time: moment(item?.value?.[0])?.format('YYYY-MM-DD'),
      end_time: moment(item?.value?.[1])?.format('YYYY-MM-DD'),
    }));
    filterConfirm?.({
      ...value,
      rangeTime: {
        name: statiscDate?.name,
        type: 'date',
        filterId: statiscDate?.filterId,
        value: statiscDate?.value
      },
      comparisonDate,
      timeType: 'Day',
      selector: mergedSelector,
      pagePath: path,
      filterDefaultSelect: pcDateComparisonDefaultSelector
    });
    // 打点，沟通后打点操作放在业务代码的confirm和reset中，不封装在新版本筛选器中
    setBtnPointLog(buttonLogConfig, {
      ...value,
      rangeTime: {
        name: statiscDate?.name,
        type: 'date',
        filterId: statiscDate?.filterId,
        value: statiscDate?.value
      },
      comparisonDate,
      timeType: 'Day'
    });
  };

  const handleReset = (value: any) => {
    filterValueRef.current = value;
    setIsReseted(true);
    // 重置时清除时间范围超限状态
    setOverRangeTimeLimitStatus({});
    const comparisonDate = [
      defaultDates.defaultStaticsDate,
      defaultDates.defaultContrastDate
    ]?.map((item: any) => ({
      name: item?.name,
      start_time: moment(item?.value?.[0])?.format('YYYY-MM-DD'),
      end_time: moment(item?.value?.[1])?.format('YYYY-MM-DD'),
    }));

    filterReset?.({
      ...value,
      rangeTime: defaultDates.defaultStaticsDate,
      comparisonDate,
      timeType: 'Day',
      selector: mergedSelector,
      pagePath: path,
      filterDefaultSelect: pcDateComparisonDefaultSelector
    });
    setStaticsDate(defaultDates.defaultStaticsDate);
    setContrastDate([defaultDates.defaultContrastDate]);
    // 打点，沟通后打点操作放在业务代码的confirm和reset中，不封装在新版本筛选器中
    setBtnPointLog(buttonLogConfig,{
      ...value,
      rangeTime: defaultDates.defaultStaticsDate,
      comparisonDate,
      timeType: 'Day'
    }, true);
  }; 

  const handleInit = (value: any) => {
    filterValueRef.current = value;
    const comparisonDate = [statiscDate, ...contrastDate]?.map((item: any) => ({
      name: item?.name,
      start_time: moment(item?.value?.[0])?.format('YYYY-MM-DD'),
      end_time: moment(item?.value?.[1])?.format('YYYY-MM-DD'),
    }));
    filterInit?.({
      ...value,
      rangeTime: statiscDate,
      comparisonDate,
      timeType: 'Day',
      selector: mergedSelector,
      pagePath: path,
      filterDefaultSelect: pcDateComparisonDefaultSelector
    });
    setFilterLoading(false);
  };

  // 处理日期范围超限状态
  const handleOverRangeTimeLimit = (dateFilterKey: string, overRangeTimeLimit: boolean) => {
    if (isNotLimitTime) return; // 无限制直接跳过
    setOverRangeTimeLimitStatus(prev => ({
      ...prev,
      [dateFilterKey]: overRangeTimeLimit
    }));
  };

  // 检查是否有任何日期范围超限
  const hasOverRangeTimeLimit = !isNotLimitTime && Object.values(overRangeTimeLimitStatus).some(status => status);

  // 获取最新2个事件设置默认日期
  useEffect(() => {
    // 防止切换游戏时，会导致默认事件被上一个游戏覆盖
    if(gameConfig?.game_code !== currentGame?.game_code) {
      return;
    }
    const eventList = gameConfig?.season_info?.date || [];
    if (eventList.length >= 2) {
      const newEventList = [...eventList];
      newEventList.sort((a: any, b: any) => {
        // 解决safari浏览器兼容问题
        const aStartData = a.start_date.replace(/-/g, "/");
        const bStartData = b.start_date.replace(/-/g, "/");
        return new Date(bStartData).getTime() - new Date(aStartData).getTime();
      });
      const isEN = getLocale() === 'en-US';

      const dates = {
        defaultStaticsDate: {
          ...defaultDates.defaultStaticsDate,
          name: isEN ? newEventList[0].name : newEventList[0].nameCN,
          value: [
            moment(newEventList[0].start_date),
            moment(newEventList[0].end_date),
          ],
        },
        defaultContrastDate: {
          ...defaultDates.defaultContrastDate,
          name: isEN ? newEventList[1].name : newEventList[1].nameCN,
          value: [
            moment(newEventList[1].start_date),
            moment(newEventList[1].end_date),
          ],
        }
      };

      setDefaultDates(dates);
      setStaticsDate({ ...dates.defaultStaticsDate });
      setContrastDate([dates.defaultContrastDate]);
    }
  }, [gameConfig?.game_code, currentGame?.game_code]);

  return <Filter
    filterHideDefaultIsReq={true}
    openSticky={openSticky}
    pageDefaultSelector={pcDateComparisonDefaultSelector}
    path={path}
    outLatestDate={latestDate}
    useFrontFilterConfig={true}
    latestDateType={''}
    value={filterValueRef.current}
    inputList={inputList}
    extraRenderContent={<>
      <DateComparesionExralFilter
        isNotLimitTime={isNotLimitTime}
        isReseted={isReseted}
        statiscDate={statiscDate}
        contrastDate={contrastDate}
        latestDate={latestDate}
        setContrastDate={setContrastDate}
        setStaticsDate={setStaticsDate}
        onOverRangeTimeLimit={handleOverRangeTimeLimit} />
    </>}
    onInit={handleInit}
    onConfirm={handleConfirm}
    onReset={handleReset}
    outSelector={mergedSelector}
    loading={loading}
    // isOutScheme={true}
    isHaveFilterSave={isHaveFilterSave}
    confirmType={hasOverRangeTimeLimit ? 'disabled' : undefined}
  >
    {children}
  </Filter>;
};

export default PcConsoleDateComparisonFilter;
